import React from 'react';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GoalOptimizerRecommendation, GoalOptimizerExplainResponse } from 'api/GoalOptimizerApi';
import { AICoachGoalDecisionType } from 'enums/AICoach';
import { WorkflowTypes } from 'handlers/aiChat';
import { recommendGoal, explainGoal, paraphraseGoal, paraphraseNextStep } from 'thunks';

export enum ActionOverrideType {
  ToggleAIChat = 'toggleAIChat',
  NavigateToOfferAvailable = 'navigateToOfferAvailable',
  PursueProbableLoan = 'pursueProbableLoan',
}

export interface Action {
  id: number;
  actionText: string;
  activateWorkflow?: WorkflowTypes;
  actionType?: string;
  isEducation?: boolean;
  isDecision?: boolean;
  isUserChoice?: boolean;
  goals?: (AICoachGoalDecisionType | string)[];
  savingsAccountId?: string;
  needsPlaid?: boolean;
  sourceActionId?: number;
  actionOverride?: ActionOverrideType;
}

export interface Message {
  explanation: string;
  actions: Action[];
  userResponse?: string | React.ReactNode;
  successMessage?: string;
  showBackButton?: boolean;
  previousActions?: Action[];
}

export interface GoalOptimizerState {
  recommendation?: GoalOptimizerRecommendation;
  recommendedPrimaryGoal?: AICoachGoalDecisionType;
  recommendedSecondaryGoal?: AICoachGoalDecisionType;
  explanation?: GoalOptimizerExplainResponse;
  messages: Message[];
  topicsCovered: string[];
  decisionActions: Action[];
  educationActions: Action[];
  lastAction?: Action;
  isLoading: boolean;
  hasAcceptedDisclosure: boolean;
  error?: any;
}

const initialState: GoalOptimizerState = {
  messages: [],
  topicsCovered: [],
  decisionActions: [],
  educationActions: [],
  isLoading: false,
  hasAcceptedDisclosure: false,
};

const goalOptimizerSlice = createSlice({
  name: 'goalOptimizer',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<Message>) => {
      state.messages.push(action.payload);
    },
    updateLastMessage: (state, action: PayloadAction<Partial<Message>>) => {
      const lastMessage = state.messages[state.messages.length - 1];
      if (lastMessage) {
        state.messages[state.messages.length - 1] = { ...lastMessage, ...action.payload };
      }
    },
    updateMessageByIndex: (state, action: PayloadAction<{ index: number; message: Partial<Message> }>) => {
      const { index, message } = action.payload;
      if (state.messages[index]) {
        state.messages[index] = { ...state.messages[index], ...message };
      }
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    addTopic: (state, action: PayloadAction<string>) => {
      if (!state.topicsCovered.includes(action.payload)) {
        state.topicsCovered.push(action.payload);
      }
    },
    removeDecisionAction: (state, action: PayloadAction<string>) => {
      // Remove decision action by text when user clicks on it
      state.decisionActions = state.decisionActions.filter(
        (decisionAction) => decisionAction.actionText !== action.payload,
      );
    },
    setLastAction: (state, action: PayloadAction<Action>) => {
      state.lastAction = action.payload;
    },
    clearLastAction: (state) => {
      state.lastAction = undefined;
    },
    setHasAcceptedDisclosure: (state, action: PayloadAction<boolean>) => {
      state.hasAcceptedDisclosure = action.payload;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(recommendGoal.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(
        recommendGoal.fulfilled,
        (state, { payload: { recommendation, recommendedPrimaryGoal, recommendedSecondaryGoal } }) => {
          state.recommendation = recommendation;
          state.recommendedPrimaryGoal = recommendedPrimaryGoal;
          state.recommendedSecondaryGoal = recommendedSecondaryGoal;

          // Separate actions into education and decision types
          const educationActions: Action[] = [];
          const decisionActions: Action[] = [];

          recommendation.actions.forEach((userAction) => {
            if (userAction.isDecision) {
              decisionActions.push(userAction);
            } else {
              educationActions.push(userAction);
            }
          });

          // Store decision and education actions to persist across messages
          state.decisionActions = decisionActions;
          state.educationActions = educationActions;

          // Add new message with all actions (education + decision)
          state.messages.push({
            explanation: recommendation.explanation,
            actions: [...educationActions, ...decisionActions],
          });
          state.isLoading = false;
        },
      )
      .addCase(recommendGoal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(explainGoal.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(explainGoal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.explanation = action.payload;

        const sourceMessage = state.messages[state.messages.length - 1];
        const remainingDecisionActions = state.decisionActions.filter((decisionAction) =>
          sourceMessage.actions.some((msgAction) => msgAction.actionText === decisionAction.actionText),
        );

        // Always add a new message with explanation and actions
        state.messages.push({
          explanation: action.payload.explanation,
          actions: [...action.payload.suggestedTopics, ...remainingDecisionActions],
        });

        // Update decisionActions to only include those that are still present
        state.decisionActions = remainingDecisionActions;
      })
      .addCase(explainGoal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(paraphraseGoal.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(paraphraseGoal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(paraphraseGoal.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(paraphraseNextStep.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(paraphraseNextStep.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(paraphraseNextStep.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      });
  },
});

export const {
  addMessage,
  updateLastMessage,
  updateMessageByIndex,
  clearMessages,
  addTopic,
  removeDecisionAction,
  setLastAction,
  clearLastAction,
  setHasAcceptedDisclosure,
  resetState: resetGoalOptimizerState,
} = goalOptimizerSlice.actions;

export default goalOptimizerSlice.reducer;
